import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Edit, 
  Trash2, 
  MoveUp, 
  MoveDown, 
  Eye,
  EyeOff,
  Upload,
  Download,
  Settings,
  Palette,
  Type,
  FileText,
  Code
} from 'lucide-react';
import Lottie from 'lottie-react';
import loadingScreenApi from '../../services/loadingScreenApi';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import AdminSectionWrapper from './AdminSectionWrapper';

const LoadingScreenManagement = () => {
  const { notify } = useNotificationHook();
  const [settings, setSettings] = useState({
    title: 'Niswa',
    description: 'Initializing application...',
    loading_text: 'Loading...',
    lottie_url: '',
    lottie_animation_data: null,
    circular_image: ''
  });
  const [lottieData, setLottieData] = useState(null);
  
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [newCard, setNewCard] = useState({
    icon: 'Code',
    icon_color: 'text-blue-500',
    title: '',
    description: '',
    sort_order: 0
  });

  // Available icons for selection
  const availableIcons = [
    'Code', 'Smartphone', 'Globe', 'Zap', 'Shield', 'Heart', 
    'Star', 'Award', 'Users', 'TrendingUp', 'Palette', 'Database',
    'Cloud', 'Target', 'Briefcase', 'DollarSign', 'MapPin', 'Check'
  ];

  // Available colors for selection
  const availableColors = [
    'text-blue-500', 'text-green-500', 'text-purple-500', 'text-red-500',
    'text-yellow-500', 'text-indigo-500', 'text-pink-500', 'text-gray-500'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch Lottie data when URL changes
  useEffect(() => {
    if (settings.lottie_url) {
      fetchLottieData(settings.lottie_url).then(setLottieData);
    } else {
      setLottieData(null);
    }
  }, [settings.lottie_url]);

  const isValidLottieData = (data) => {
    // Check if the data has the required Lottie animation structure
    return data && 
           typeof data === 'object' && 
           data.v && // version
           data.fr && // frame rate
           data.w && // width
           data.h && // height
           Array.isArray(data.layers); // layers array
  };

  const fetchLottieData = async (lottieUrl) => {
    try {
      if (lottieUrl && lottieUrl.endsWith('.json')) {
        // For JSON files, fetch and parse
        const response = await fetch(lottieUrl);
        if (response.ok) {
          const data = await response.json();
          if (isValidLottieData(data)) {
            return data;
          } else {
            console.log('Invalid Lottie JSON data structure');
            return null;
          }
        }
      } else if (lottieUrl && lottieUrl.endsWith('.lottie')) {
        // For .lottie files, check content type first
        try {
          const response = await fetch(lottieUrl);
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
              // If it's actually JSON with .lottie extension
              const data = await response.json();
              if (isValidLottieData(data)) {
                return data;
              }
            } else if (contentType && contentType.includes('application/zip')) {
              // If it's a compressed .lottie file, we can't handle it directly
              console.log('Compressed .lottie file detected - not supported by lottie-react library');
              console.log('Please use a JSON Lottie file or convert the .lottie file to JSON format');
              return null;
            } else {
              // Try to parse as text/JSON anyway
              const text = await response.text();
              try {
                const data = JSON.parse(text);
                if (isValidLottieData(data)) {
                  return data;
                }
              } catch (parseError) {
                console.log('Lottie file is not JSON format, skipping');
                return null;
              }
            }
          }
        } catch (parseError) {
          console.log('Error processing .lottie file:', parseError);
          return null;
        }
      }
    } catch (error) {
      console.error('Error fetching Lottie data:', error);
    }
    return null;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsResponse, cardsResponse] = await Promise.all([
        loadingScreenApi.getSettings(),
        loadingScreenApi.getCards()
      ]);

      if (settingsResponse.success) {
        const settingsData = settingsResponse.data.settings;
        setSettings(settingsData);
        
        // Fetch Lottie data if URL is provided
        if (settingsData.lottie_url) {
          const lottieAnimationData = await fetchLottieData(settingsData.lottie_url);
          setLottieData(lottieAnimationData);
        }
      }
      
      if (cardsResponse.success) {
        setCards(cardsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsSave = async () => {
    try {
      setSaving(true);
      await loadingScreenApi.updateSettings(settings);
      notify.success('Settings saved successfully!');
    } catch (error) {
      notify.error('Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCardSave = async (cardData) => {
    try {
      setSaving(true);
      if (editingCard) {
        await loadingScreenApi.updateCard(editingCard.id, cardData);
        notify.success('Card updated successfully!');
      } else {
        await loadingScreenApi.createCard(cardData);
        notify.success('Card created successfully!');
      }
      setShowCardForm(false);
      setEditingCard(null);
      setNewCard({
        icon: 'Code',
        icon_color: 'text-blue-500',
        title: '',
        description: '',
        sort_order: 0
      });
      fetchData();
    } catch (error) {
      notify.error('Error saving card: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCardDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this card?')) {
      try {
        await loadingScreenApi.deleteCard(id);
        notify.success('Card deleted successfully!');
        fetchData();
      } catch (error) {
        notify.error('Error deleting card: ' + error.message);
      }
    }
  };

  const handleCardEdit = (card) => {
    setEditingCard(card);
    setNewCard({
      icon: card.icon,
      icon_color: card.icon_color,
      title: card.title,
      description: card.description,
      sort_order: card.sort_order
    });
    setShowCardForm(true);
  };

  const handleCardSort = async (cardId, direction) => {
    const currentIndex = cards.findIndex(card => card.id === cardId);
    if (currentIndex === -1) return;

    const newCards = [...cards];
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < newCards.length) {
      // Swap cards
      [newCards[currentIndex], newCards[newIndex]] = [newCards[newIndex], newCards[currentIndex]];
      
      // Update sort orders
      newCards.forEach((card, index) => {
        card.sort_order = index + 1;
      });

      try {
        await loadingScreenApi.updateCardSort(newCards);
        setCards(newCards);
      } catch (error) {
        notify.error('Error updating sort order: ' + error.message);
      }
    }
  };

  const toggleCardActive = async (card) => {
    try {
      await loadingScreenApi.updateCard(card.id, {
        ...card,
        is_active: !card.is_active
      });
      fetchData();
    } catch (error) {
      notify.error('Error updating card: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AdminSectionWrapper>
      {/* Settings Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            Loading Screen Settings
          </h2>
          <button
            onClick={handleSettingsSave}
            disabled={saving}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
          {/* Settings Form */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Settings</h3>
            <div className="grid grid-cols-1 gap-4 sm:p-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Type className="w-4 h-4 inline mr-1" />
              Title
            </label>
            <input
              type="text"
              value={settings.title}
              onChange={(e) => setSettings({ ...settings, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Enter title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Description
            </label>
            <input
              type="text"
              value={settings.description}
              onChange={(e) => setSettings({ ...settings, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Enter description"
            />
          </div>

          {/* Loading Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Loading Text
            </label>
            <input
              type="text"
              value={settings.loading_text}
              onChange={(e) => setSettings({ ...settings, loading_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="Enter loading text"
            />
          </div>

          {/* Lottie URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Upload className="w-4 h-4 inline mr-1" />
              Lottie URL (Optional)
            </label>
            <input
              type="url"
              value={settings.lottie_url || ''}
              onChange={(e) => setSettings({ ...settings, lottie_url: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="https://lottiefiles.com/..."
            />
          </div>

          {/* Circular Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Upload className="w-4 h-4 inline mr-1" />
              Circular Image (Optional)
            </label>
            <input
              type="url"
              value={settings.circular_image || ''}
              onChange={(e) => setSettings({ ...settings, circular_image: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="https://example.com/image.png"
            />
            <p className="text-xs text-gray-500 mt-1">
              This image will appear in the circular area above the title
            </p>
          </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center mb-4">
          <Eye className="w-5 h-5 mr-2" />
          Live Preview
        </h2>
        <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-lg p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="text-center">
            {/* Logo/Brand Section */}
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto mb-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                {settings.circular_image ? (
                  <img 
                    src={settings.circular_image} 
                    alt={settings.title} 
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : lottieData && isValidLottieData(lottieData) ? (
                  <Lottie 
                    animationData={lottieData}
                    loop={true}
                    autoplay={true}
                    style={{ width: 48, height: 48 }}
                  />
                ) : settings.lottie_animation_data && isValidLottieData(settings.lottie_animation_data) ? (
                  <Lottie 
                    animationData={settings.lottie_animation_data}
                    loop={true}
                    autoplay={true}
                    style={{ width: 48, height: 48 }}
                  />
                ) : (
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Code className="w-6 h-6 text-blue-500" />
                  </div>
                )}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {settings.title || 'Niswa'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {settings.description || 'Loading amazing experiences...'}
              </p>
            </div>

            {/* Progress Bar Preview */}
            <div className="w-48 mx-auto mb-4">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-1.5 rounded-full"
                  style={{ width: '65%' }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>{settings.loading_text || 'Loading...'}</span>
                <span>65%</span>
              </div>
            </div>

            {/* Lottie Preview */}
            {settings.lottie_url && (
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                {lottieData && isValidLottieData(lottieData) ? (
                  <Lottie 
                    animationData={lottieData}
                    loop={true}
                    autoplay={true}
                    style={{ width: 60, height: 60 }}
                  />
                ) : settings.lottie_animation_data && isValidLottieData(settings.lottie_animation_data) ? (
                  <Lottie 
                    animationData={settings.lottie_animation_data}
                    loop={true}
                    autoplay={true}
                    style={{ width: 60, height: 60 }}
                  />
                ) : (
                  <span className="text-xs text-gray-500">Unsupported Lottie Format</span>
                )}
              </div>
            )}

            {/* Animated Dots Preview */}
            <div className="flex justify-center items-center space-x-1 mb-4">
              {[0, 1, 2].map((dot) => (
                <div key={dot} className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 animate-pulse" />
                  {dot < 2 && (
                    <div className="w-6 h-0.5 bg-gradient-to-r from-blue-500 to-purple-600 mx-1 animate-pulse" />
                  )}
                </div>
              ))}
            </div>

            {/* Feature Cards Preview */}
            <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
              {cards.slice(0, 3).map((card, index) => (
                <div key={card.id} className="text-center">
                  <div className={`w-6 h-6 mx-auto mb-1 rounded flex items-center justify-center ${card.icon_color} bg-gray-100 dark:bg-gray-700 text-xs`}>
                    {card.icon}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{card.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Cards Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <Palette className="w-5 h-5 mr-2" />
            Feature Cards
          </h2>
          <button
            onClick={() => setShowCardForm(true)}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Card
          </button>
        </div>

        {/* Cards List */}
        <div className="space-y-4">
          {cards.map((card, index) => (
            <div key={card.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleCardSort(card.id, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-500 hover:text-blue-500 disabled:opacity-50"
                    >
                      <MoveUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleCardSort(card.id, 'down')}
                      disabled={index === cards.length - 1}
                      className="p-1 text-gray-500 hover:text-blue-500 disabled:opacity-50"
                    >
                      <MoveDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.icon_color} bg-gray-100 dark:bg-gray-700`}>
                      <span className="text-sm font-bold">{card.icon}</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{card.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{card.description}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleCardActive(card)}
                    className={`p-2 rounded-lg ${card.is_active ? 'text-green-500' : 'text-gray-400'}`}
                    title={card.is_active ? 'Active' : 'Inactive'}
                  >
                    {card.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  
                  <button
                    onClick={() => handleCardEdit(card)}
                    className="p-2 text-blue-500 hover:text-blue-600 rounded-lg"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleCardDelete(card.id)}
                    className="p-2 text-red-500 hover:text-red-600 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Card Form Modal */}
      {showCardForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingCard ? 'Edit Card' : 'Add New Card'}
            </h3>
            
            <div className="space-y-4">
              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon
                </label>
                <select
                  value={newCard.icon}
                  onChange={(e) => setNewCard({ ...newCard, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {availableIcons.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>

              {/* Icon Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Icon Color
                </label>
                <select
                  value={newCard.icon_color}
                  onChange={(e) => setNewCard({ ...newCard, icon_color: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {availableColors.map(color => (
                    <option key={color} value={color}>{color.replace('text-', '').replace('-500', '')}</option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={newCard.title}
                  onChange={(e) => setNewCard({ ...newCard, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={newCard.description}
                  onChange={(e) => setNewCard({ ...newCard, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter description"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCardForm(false);
                  setEditingCard(null);
                  setNewCard({
                    icon: 'Code',
                    icon_color: 'text-blue-500',
                    title: '',
                    description: '',
                    sort_order: 0
                  });
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCardSave(newCard)}
                disabled={saving || !newCard.title || !newCard.description}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : (editingCard ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminSectionWrapper>
  );
};

export default LoadingScreenManagement; 